/**
 * iter-235 P0 — Shipment State + CTA matrix (frontend SSOT mirror).
 *
 * This file is the ONLY place in the frontend allowed to derive
 * "which buttons should I show for an order?". Every surface
 * (Order Details, Orders List row overflow, Pack & Ship row
 * overflow, Shipping Pipeline row overflow) must import the
 * CTA list from here and render buttons by key — no surface
 * is allowed to invent its own state machine.
 *
 * The backend at /api/shipping/cta-matrix is the source of truth.
 * The constants below are a static fallback so that:
 *   1. The UI renders correctly if the matrix call hasn't returned yet.
 *   2. Static analysis (PropTypes, tests) can validate at build time.
 *
 * If the backend hands a key we don't recognise, we render NO button
 * for that key (fail-safe — never invent an action).
 */

// Canonical state constants — keep in sync with services/shipment_state_resolver.py
export const SHIPMENT_STATES = {
  READY_TO_SHIP:    "READY_TO_SHIP",
  ACTIVE_AWB:       "ACTIVE_AWB",
  PICKUP_SCHEDULED: "PICKUP_SCHEDULED",
  IN_TRANSIT:       "IN_TRANSIT",
  DELIVERED:        "DELIVERED",
  AWB_CANCELLED:    "AWB_CANCELLED",
  RETURNED:         "RETURNED",
  ORDER_CLOSED:     "ORDER_CLOSED",
  NOT_APPLICABLE:   "NOT_APPLICABLE",
};

// Action keys — single contract between resolver and UI.
export const SHIPMENT_ACTIONS = {
  SHIP_WITH_DELHIVERY:   "ship_with_delhivery",
  PRINT_LABEL:           "print_label",
  SCHEDULE_PICKUP:       "schedule_pickup",
  CANCEL_AWB:            "cancel_awb",
  TRACK_SHIPMENT:        "track_shipment",
  VIEW_SHIPMENT_HISTORY: "view_shipment_history",
};

// Fallback matrix — must match `CTA_MATRIX` in the resolver exactly.
// Lives here so the UI keeps working if /cta-matrix call is slow.
export const FALLBACK_CTA_MATRIX = {
  READY_TO_SHIP:    ["ship_with_delhivery"],
  ACTIVE_AWB:       ["print_label", "schedule_pickup", "cancel_awb"],
  PICKUP_SCHEDULED: ["print_label", "track_shipment"],
  IN_TRANSIT:       ["track_shipment"],
  DELIVERED:        [],
  AWB_CANCELLED:    ["ship_with_delhivery"],
  RETURNED:         ["view_shipment_history"],
  ORDER_CLOSED:     [],
  NOT_APPLICABLE:   [],
};

// Static labels — used only as a fallback when backend hasn't responded.
export const FALLBACK_STATE_LABELS = {
  READY_TO_SHIP:    "Ready to Ship",
  ACTIVE_AWB:       "AWB Generated",
  PICKUP_SCHEDULED: "Pickup Scheduled",
  IN_TRANSIT:       "In Transit",
  DELIVERED:        "Delivered",
  AWB_CANCELLED:    "AWB Cancelled",
  RETURNED:         "Returned",
  ORDER_CLOSED:     "Order Closed",
  NOT_APPLICABLE:   "Not Shippable",
};

// Color name → Tailwind classes for badge rendering.
// This indirection is intentional: the resolver hands semantic
// colors (red, blue, …) so the UI can re-theme without backend
// changes.
export const STATE_BADGE_CLASSES = {
  blue:    "bg-blue-100 text-blue-800 border border-blue-200",
  emerald: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  amber:   "bg-amber-100 text-amber-800 border border-amber-200",
  indigo:  "bg-indigo-100 text-indigo-800 border border-indigo-200",
  green:   "bg-green-100 text-green-800 border border-green-200",
  red:     "bg-red-100 text-red-800 border border-red-200",
  orange:  "bg-orange-100 text-orange-800 border border-orange-200",
  gray:    "bg-gray-100 text-gray-700 border border-gray-200",
};

// Human-readable button labels for each action — surfaces render
// these or override with their own (e.g. icon-only on small screens).
export const ACTION_LABELS = {
  ship_with_delhivery:   "Ship Now",
  print_label:           "Print Label",
  schedule_pickup:       "Schedule Pickup",
  cancel_awb:            "Cancel AWB",
  track_shipment:        "Track Shipment",
  view_shipment_history: "View Shipment History",
};


/**
 * Read the SSOT fields off an order/shipment dict. Falls back to the
 * legacy raw-field derivation if the backend hasn't stamped the new
 * fields yet (forward compatibility during the iter-235 rollout).
 *
 * @param {object} item — order or shipment dict from the API
 * @returns {{state: string, label: string, color: string, ctas: string[]}}
 */
export function readShipmentState(item) {
  if (!item) {
    return {
      state: SHIPMENT_STATES.READY_TO_SHIP,
      label: FALLBACK_STATE_LABELS.READY_TO_SHIP,
      color: "blue",
      ctas:  [...FALLBACK_CTA_MATRIX.READY_TO_SHIP],
    };
  }
  // Preferred path — backend stamped the 4 SSOT fields.
  if (item.shipment_state) {
    return {
      state: item.shipment_state,
      label: item.shipment_state_label
              || FALLBACK_STATE_LABELS[item.shipment_state]
              || item.shipment_state,
      color: item.shipment_state_color || "gray",
      ctas:  Array.isArray(item.shipment_ctas)
              ? item.shipment_ctas
              : (FALLBACK_CTA_MATRIX[item.shipment_state] || []),
    };
  }
  // Fallback — derive locally if backend hasn't been redeployed yet.
  // Mirrors the resolver's order-doc-only branch.
  const state = deriveLegacyState(item);
  return {
    state,
    label: FALLBACK_STATE_LABELS[state] || state,
    color: ({
      READY_TO_SHIP: "blue", ACTIVE_AWB: "emerald",
      PICKUP_SCHEDULED: "amber", IN_TRANSIT: "indigo",
      DELIVERED: "green", AWB_CANCELLED: "red",
      RETURNED: "orange", ORDER_CLOSED: "gray", NOT_APPLICABLE: "gray",
    }[state]) || "gray",
    ctas: [...(FALLBACK_CTA_MATRIX[state] || [])],
  };
}


/**
 * Legacy state derivation — only invoked when the backend hasn't
 * stamped shipment_state yet. Kept minimal; the real logic lives
 * server-side. Must be removed once the FE+BE are deployed together.
 */
function deriveLegacyState(item) {
  const status = String(item.status || "").toLowerCase();
  if (["cancelled", "canceled", "refunded", "void"].includes(status)) {
    return SHIPMENT_STATES.ORDER_CLOSED;
  }
  const productType = String(item.product_type || item.listing_type || "").toLowerCase();
  if (["digital", "booking", "service", "rental_digital"].includes(productType)) {
    return SHIPMENT_STATES.NOT_APPLICABLE;
  }
  const sh = item.shipment || {};
  const shStatus = String(sh.status || "").toLowerCase();
  const pickupStatus = String(sh.pickup_status || "").toLowerCase();
  const awbCancelled = item.awb_cancelled || sh.awb_cancelled;

  if (awbCancelled || ["cancelled", "canceled"].includes(shStatus)
      || item.last_cancelled_awb) {
    return SHIPMENT_STATES.AWB_CANCELLED;
  }
  if (["rto", "returned", "rto_initiated", "rto_in_transit"].includes(shStatus)) {
    return SHIPMENT_STATES.RETURNED;
  }
  if (["delivered", "dl", "dlv", "completed"].includes(shStatus)) {
    return SHIPMENT_STATES.DELIVERED;
  }
  if (["in_transit", "intransit", "shipped", "out_for_delivery", "ofd",
       "picked_up", "dispatched"].includes(shStatus)
      || pickupStatus === "picked_up") {
    return SHIPMENT_STATES.IN_TRANSIT;
  }
  if (["pickup_scheduled", "pickup_pending", "manifested"].includes(pickupStatus)
      || sh.pickup_scheduled_at) {
    return SHIPMENT_STATES.PICKUP_SCHEDULED;
  }
  const awb = item.awb_number || item.awb || sh.awb || item.tracking_number;
  if (awb && !["", "none", "null", "pending", "n/a"].includes(String(awb).toLowerCase())) {
    return SHIPMENT_STATES.ACTIVE_AWB;
  }
  return SHIPMENT_STATES.READY_TO_SHIP;
}


/**
 * Given an item, return only the action keys to render. The result is
 * a stable, deduped list — overflow menus iterate over it directly.
 */
export function getShipmentCtas(item) {
  return readShipmentState(item).ctas;
}


/**
 * Check whether a specific action should be shown for an item.
 * Useful inline (e.g. `hasCta(order, 'cancel_awb') && <Button…>`).
 */
export function hasCta(item, actionKey) {
  return getShipmentCtas(item).includes(actionKey);
}
